import { observer } from 'mobx-react-lite';
import React from 'react';
import { withDefaultProps } from 'react-default-props-context';
import { ExplanationListViewItem } from 'src/answers/components/ExplanationListViewItem';
import { GoToNextExplanationButton } from 'src/answers/components/GoToNextExplanationButton';
import { ExplanationT } from 'src/api/types/ExplanationT';
import { ScoreRangeT } from 'src/api/types/ScoreRangeT';
import { Portal } from 'src/frames/components/Portal';
import { useScrollToTop } from 'src/frames/components/useScrollToTop';
import { EmergencyIcon } from 'src/frames/icons/EmergencyIcon';
import { PhoneIcon } from 'src/frames/icons/PhoneIcon';
import { UrlIcon } from 'src/frames/icons/UrlIcon';
import { cn } from 'src/frames/utils/cn';
import { BackButton } from 'src/questions/components/BackButton';
import { SourcesView } from 'src/summary/components/SourcesView';
import './SummaryView.scss';

type PropsT = {
  className?: any;
};

type DefaultPropsT = {
  getScoreRange: (score: number) => ScoreRangeT;
  totalScore: number;
  portalElm: any;
  riskfactors: ExplanationT[];
  tips: ExplanationT[];
};

export const SummaryView = observer(
  withDefaultProps<PropsT, DefaultPropsT>((props: PropsT & DefaultPropsT) => {
    useScrollToTop('SelfTestView', 'SummaryView');
    const [showSources, setShowSources] = React.useState(false);

    const scoreRange = props.getScoreRange(props.totalScore);

    const riskfactorsDiv = props.riskfactors.length ? (
      <ExplanationListViewItem
        label="Ihre Risikofaktoren"
        className="my-2"
        explanation={props.riskfactors[0]}
        explanationPageIdx={0}
      />
    ) : null;

    const tipsDiv = props.tips.length ? (
      <ExplanationListViewItem
        label="Ihre Tipps"
        className="my-2"
        explanation={props.tips[0]}
        explanationPageIdx={props.riskfactors.length}
      />
    ) : null;

    const isEmergency =
      !!scoreRange.emergencyPhoneNr || !!scoreRange.emergencyUrl;

    const emergencyDiv = isEmergency ? (
      <div className={cn('SummaryView__Emergency', 'flex flex-col')}>
        {!!scoreRange.emergencyPhoneNr && (
          <div
            className={cn('SummaryView__EmergencyRow', 'flex flex-row', 'mb-4')}
          >
            <PhoneIcon className="mr-4" />
            {scoreRange.emergencyPhoneNr}
          </div>
        )}
        {!!scoreRange.emergencyUrl && (
          <div
            className={cn('SummaryView__EmergencyRow', 'flex flex-row', 'mb-4')}
          >
            <UrlIcon className="mr-4" />
            <a target="_blank" href={scoreRange.emergencyUrl}>
              {scoreRange.emergencyUrl}
            </a>
          </div>
        )}
      </div>
    ) : null;

    return showSources ? (
      <SourcesView setShowSources={setShowSources} className="flex-1" />
    ) : (
      <div className={cn('SummaryView', 'flex flex-col', props.className)}>
        <Portal elm={props.portalElm}>
          <BackButton className="mr-6" />
          <div className={cn('SummaryView__SuperTitleInPortal', 'flex-1')}>
            Ihr Ergebnis
          </div>
          <GoToNextExplanationButton
            nextExplanationPageIdx={0}
            className={cn('ml-6', {
              invisible: !(props.riskfactors.length || props.tips.length),
            })}
          />
        </Portal>
        <div
          className={cn(
            'SummaryView__Card',
            'flex flex-col',
            'flex-1',
            'px-4 py-2'
          )}
        >
          <div
            className={cn('SummaryView__Title', 'flex flex-row', {
              'SummaryView__Title--emergency': isEmergency,
              'px-4 pt-[13px] pb-[9px] mb-2': isEmergency,
            })}
          >
            {isEmergency && <EmergencyIcon className="mr-4" />}
            {`${scoreRange.name}`}
          </div>
          <div className={cn('SummaryView__Description', 'mt-2 mb-4')}>
            {scoreRange.description}
          </div>
          {emergencyDiv}
          {riskfactorsDiv}
          {tipsDiv}
          <div className="basis-0 grow shrink"></div>
          <div
            className={cn(
              'SummaryView__LinkToReferences',
              'mt-4 mb-2',
              'self-center'
            )}
            onClick={() => setShowSources(true)}
          >
            Autoren- & Quelleninformationen
          </div>
        </div>
      </div>
    );
  })
);
